import { ReactNode, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import commonEssence from '@sorare/core/src/assets/rewards/common-essence.png';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { ANY_SPORT_COMMON_MARKET } from '@sorare/core/src/constants/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';

import { LobbyTile } from 'components/home/LobbyTile';

import bigCommonMarketBg from './assets/bigCommonMarketBg.jpg';
import commonSwapSquare from './assets/commonSwapSquare.png';

const Wrapper = styled.div`
  opacity: 1;
  position: relative;

  transition: opacity 0.05s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const StyledLobbyTile = styled(LobbyTile)`
  display: flex;
  height: 100%;
  align-items: end;

  background: url(${bigCommonMarketBg});
  background-size: cover;
  background-position: center;
`;

const CTA = styled(Button)`
  margin-left: auto;
`;

const Resume = styled(Horizontal)`
  justify-content: flex-start;
`;

const CommonEssenceIcon = styled.img.attrs({ src: commonEssence, alt: '' })`
  height: 1.1em;
`;

type VariantProps = {
  userCommonMarketResume: ReactNode;
};

const CompactVariant = ({ userCommonMarketResume }: VariantProps) => {
  const { generateSportContextPath } = useSportContext();
  return (
    <Wrapper>
      <LinkOverlay
        as={Link}
        to={generateSportContextPath(ANY_SPORT_COMMON_MARKET)}
      >
        <Horizontal>
          <img width={48} height={48} src={commonSwapSquare} alt="" />
          <Vertical>
            <LabelM bold>
              <FormattedMessage {...glossary.commonMarket} />
            </LabelM>
            {userCommonMarketResume}
          </Vertical>
          <CTA size="compact" color="quaternary">
            <FormattedMessage
              id="ForYou.CommonMarket.CTA"
              defaultMessage="Swap Now"
            />
          </CTA>
        </Horizontal>
      </LinkOverlay>
    </Wrapper>
  );
};
const DefaultVariant = ({ userCommonMarketResume }: VariantProps) => {
  const { generateSportContextPath } = useSportContext();
  return (
    <StyledLobbyTile
      outlineColor="var(--c-nd-900-opaque)"
      labelColor="var(--c-nd-100-opaque)"
      label={
        <LabelS bold color="var(--c-white)">
          <FormattedMessage
            id="ForYou.CommonMarket.label"
            defaultMessage="Card swap"
          />
        </LabelS>
      }
    >
      <LinkOverlay
        as={Link}
        to={generateSportContextPath(ANY_SPORT_COMMON_MARKET)}
      >
        <LabelL bold>
          <FormattedMessage {...glossary.commonMarket} />
        </LabelL>
        {userCommonMarketResume}
      </LinkOverlay>
    </StyledLobbyTile>
  );
};

type Props = {
  compact?: boolean;
};

export const CommonMarket = ({ compact }: Props) => {
  const navigate = useNavigate();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { currentUser } = useCurrentUserContext();
  const { sport, generateSportContextPath } = useSportContext();
  const pointsCount = currentUser?.commonCardPoints || 0;
  const cardsCount = currentUser?.cardCounts?.common || 0;
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const dailyClaimButtonLifecyclePerSport = {
    [Sport.NBA]: lifecycle?.sawDailyClaimButton,
    [Sport.FOOTBALL]: lifecycle?.sawDailyClaimButton,
    [Sport.BASEBALL]: lifecycle?.sawMLBDailyClaimButton,
  };
  const shouldShowOnboarding =
    !lifecycle?.sawGalleryOnboarding &&
    dailyClaimButtonLifecyclePerSport[sport];

  const userCommonMarketResume = useMemo(() => {
    return (
      <Resume gap={1}>
        <Horizontal gap={0.5}>
          <CommonEssenceIcon />
          <LabelM color="var(--c-nd-600)">
            <FormattedNumber value={pointsCount} />
          </LabelM>
        </Horizontal>
        <LabelM as="span" color="var(--c-nd-400)">
          •
        </LabelM>
        <Horizontal gap={0.5}>
          <ScarcityIcon scarcity={Rarity.common} />
          <LabelM
            as="span"
            color={cardsCount < 9 ? 'var(--c-yellow-400)' : 'var(--c-nd-600)'}
          >
            {cardsCount}
          </LabelM>
        </Horizontal>
      </Resume>
    );
  }, [cardsCount, pointsCount]);

  return (
    <div>
      <OnboardingHighlightWrapper
        highlightName="MyCardsCommonMarket"
        config={
          compact
            ? { scrollIntoView: true }
            : {
                border: true,
                borderRadius: 'var(--double-unit)',
                scrollIntoView: true,
              }
        }
      >
        {compact ? (
          <CompactVariant userCommonMarketResume={userCommonMarketResume} />
        ) : (
          <DefaultVariant userCommonMarketResume={userCommonMarketResume} />
        )}
      </OnboardingHighlightWrapper>
      {shouldShowOnboarding && (
        <HighlightOverlay
          onClose={() => {
            updateLifecycle(LIFECYCLE.sawGalleryOnboarding, true);
            navigate(generateSportContextPath(ANY_SPORT_COMMON_MARKET));
          }}
          extras={[
            {
              nextTo: 'MyCardsCommonMarket',
              render: props => (
                <ExtraMessageWithBlurredBg
                  align="center"
                  anchor="bottom"
                  positionProps={props}
                  fullWidth={!isTabletAndAbove}
                >
                  <LabelL>
                    <FormattedMessage
                      id="CommonMarket.onboarding.title"
                      defaultMessage="Common Cards are free! You can swap yours here when you’re ready"
                    />
                  </LabelL>
                </ExtraMessageWithBlurredBg>
              ),
            },
          ]}
          highlightNames={['MyCardsCommonMarket']}
        />
      )}
    </div>
  );
};
